import { useState } from 'react'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import classNames from 'classnames'

import { RootState } from '../../../appReducer'
import { AccountTypes } from '../../../models/enums'
import { useAppDispatch } from '../../../util/useAppDispatch'
import { impersonateUser, resendInvitation, resendInvitationV2, resetPassword, saveAccount } from '../actions'
import { restoreAccount } from '../v2actions'
import { fetchPracticeAccount, fetchPracticeAccountLocations } from '../v2actions'

import StaffConfirmationBox from './StaffConfirmationBox'
import { StaffConfirmationBoxType } from './StaffConfirmationBox'
import StaffForm from './StaffForm'
import StaffSuccessBox, { StaffSuccessBoxType } from './StaffSuccessBox'

import './StaffMember.sass'

export type Props = {
    type: 'edit' | 'menu'
    practice: Models.Practice
    employee: ModelsV2.Practice.PracticeStaffData
    refreshStaffList: () => void
}

const StaffMemberActions = (props: Props) => {
    const account = useSelector((state: RootState) => state.app.self && state.app.self.account)
    const practiceUserRoles = useSelector((state: RootState) => state.practices.userRoles)
    const pstUserManagement = !useSelector((state: RootState) => state.app.featureFlags?.pstUserManagement)

    const [successBox, setSuccessBox] = useState<StaffSuccessBoxType | ''>('')
    const [showModal, setShowModal] = useState(false)
    const [showActions, setShowActions] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)
    const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | undefined>(undefined)
    const [showConfirmationBox, setShowConfirmationBox] = useState(false)
    const [confirmationBoxCallbackFn, setConfirmationBoxCallbackFn] = useState<(() => void) | undefined>(undefined)
    const [confirmationBoxType, setConfirmationBoxType] = useState<StaffConfirmationBoxType | undefined>(undefined)

    const dispatch = useAppDispatch()

    const { employee, practice } = props

    const hasStatus = (status: string) => {
        return employee && employee.account_status?.value === status
    }

    const onCloseModal = () => {
        props.refreshStaffList()
        setShowModal(false)
    }

    const onShowModal = async () => {
        setModalLoading(true)
        await dispatch(fetchPracticeAccount(practice.id, employee.id))
        await dispatch(fetchPracticeAccountLocations(practice.id, employee.id, 1))
        setModalLoading(false)
        setShowModal(true)
    }

    const onOpenActionsMenu = async (event: any) => {
        setShowActions(true)
        setActionsAnchor(event.currentTarget)
        await dispatch(fetchPracticeAccount(practice.id, employee.id))
    }

    const onCloseActionsMenu = () => {
        setShowActions(false)
        setActionsAnchor(undefined)
    }

    const onShowCloseConfirmationBox = (type: StaffConfirmationBoxType, callback: () => void) => {
        setConfirmationBoxType(type)
        setConfirmationBoxCallbackFn(() => callback)
        setShowConfirmationBox(true)
    }

    const onCloseConfirmationBox = () => {
        setConfirmationBoxType(undefined)
        setConfirmationBoxCallbackFn(() => undefined)
        setShowConfirmationBox(false)
    }

    const createReqPayload = (status: string) => {
        return {
            firstName: employee.first_name,
            lastName: employee.last_name,
            email: employee.username,
            permissions: employee.permission?.map(p => p.type) || [],
            role: employee.simplifeye_role.value,
            locations: [],
            emails: (employee as ModelsV2.Practice.PracticeAccount).emails,
            phoneNumbers: (employee as ModelsV2.Practice.PracticeAccount).phone_numbers,
            accountStatus: status,
        }
    }

    const onImpersonate = () => {
        onCloseActionsMenu()
        const callback = () => {
            dispatch(impersonateUser(employee, practice.id))
        }
        onShowCloseConfirmationBox('impersonateUser', callback)
    }

    const onToggleAccountStatus = () => {
        const employeeStatusValue = employee.account_status?.value
        const shouldDeactivateAccount = employeeStatusValue === 'active' || employeeStatusValue === 'invited'

        const callback = async () => {
            const changes = createReqPayload(shouldDeactivateAccount ? 'deactivated' : 'active')
            await dispatch(saveAccount(practice, employee.id, changes))
            props.refreshStaffList()
        }

        const confirmationType = shouldDeactivateAccount ? 'deactivateAccount' : 'reactivateAccount'
        onShowCloseConfirmationBox(confirmationType, callback)
        onCloseActionsMenu()
    }

    const onDeleteAccount = () => {
        const callback = async () => {
            const changes = createReqPayload('deleted')
            await dispatch(saveAccount(practice, employee.id, changes))
            props.refreshStaffList()
        }
        onShowCloseConfirmationBox('deleteAccount', callback)
        onCloseActionsMenu()
    }

    const onRestoreAccount = () => {
        const callback = async () => {
            await dispatch(restoreAccount(practice, employee))
            props.refreshStaffList()
        }
        onShowCloseConfirmationBox('restoreAccount', callback)
        onCloseActionsMenu()
    }

    const onInviteResend = () => {
        const callback = () => {
            if (employee) {
                const changes: Api.InviteResend = {
                    username: employee.username,
                    practice_id: practice.id,
                }
                const changesV2: ApiV2.Practice.InviteResend = {
                    email: employee.username,
                    practiceId: practice.id,
                }
                dispatch(employee?.is_sso ? resendInvitationV2(changesV2) : resendInvitation(changes))
            }
        }
        onShowCloseConfirmationBox('resendInvitation', callback)
        onCloseActionsMenu()
    }

    const onResetPassword = () => {
        const callback = async () => {
            const changes: ApiV2.Practice.ResetPassword = {
                email: employee.username,
            }
            const resetResponse = await dispatch(resetPassword(changes))
            if (resetResponse.success) {
                onShowSuccessBox('resetPassword')
            }
        }
        onShowCloseConfirmationBox('resetPassword', callback)
        onCloseActionsMenu()
    }

    const onCloseSuccessBox = () => {
        setSuccessBox('')
    }

    const onShowSuccessBox = (type: StaffSuccessBoxType) => {
        setSuccessBox(type)
    }

    const onSave = (changes: ApiV2.Practice.AccountUpdate) => {
        dispatch(saveAccount(practice, employee.id, changes)).then(onCloseModal)
    }

    const onRestore = () => {
        dispatch(restoreAccount(practice, employee)).then(onCloseModal)
    }

    const isSSO = Boolean(employee?.is_sso)
    const isAdmin =
        (account && account.type.id === AccountTypes.SimplifeyeStaff) ||
        (account && account.type.id === AccountTypes.SuperAdmin) ||
        (account && account.type.id === AccountTypes.PattersonStaff)

    const noDeleteOption = isSSO || hasStatus('deleted')
    const showResetPassword =
        !hasStatus('deactivated') && !hasStatus('invited') && !hasStatus('deleted') && pstUserManagement
    const showDeactivate = isSSO
        ? hasStatus('active') || hasStatus('invited')
        : hasStatus('active') && pstUserManagement
    const showReactivate = hasStatus('deactivated') && pstUserManagement

    const menuId = `actions-menu-${account && account.id}`

    if (props.type === 'edit') {
        return (
            <>
                <div className="edit-btn" onClick={onShowModal}>
                    {modalLoading ? (
                        <CircularProgress className="circular-progress-spinner" size={20} thickness={6} />
                    ) : (
                        <i className="material-icons settings-icon">settings</i>
                    )}
                </div>
                {showModal && (
                    <Modal
                        className="pst-modal"
                        open={true}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                                onCloseModal()
                            }
                        }}
                        disableEscapeKeyDown={true}
                    >
                        <StaffForm
                            employee={employee}
                            onSave={onSave}
                            onRestore={onRestore}
                            onClose={onCloseModal}
                            practiceUserRoles={practiceUserRoles}
                            practice={practice}
                        />
                    </Modal>
                )}
            </>
        )
    }

    return (
        <div>
            {(pstUserManagement || (!pstUserManagement && isAdmin && !hasStatus('invited'))) && (
                <IconButton
                    size="large"
                    aria-owns={actionsAnchor ? menuId : undefined}
                    aria-haspopup="true"
                    onClick={onOpenActionsMenu}
                >
                    <i className="material-icons more-icon">more_horiz</i>
                </IconButton>
            )}
            <Menu
                id={menuId}
                className="staff-member-actions-menu"
                anchorEl={actionsAnchor}
                open={showActions}
                onClose={onCloseActionsMenu}
                anchorOrigin={{
                    horizontal: 'left',
                    vertical: 'bottom',
                }}
            >
                {isAdmin && !hasStatus('invited') && (
                    <MenuItem className="staff-member-menu-item" key="impersonate" onClick={onImpersonate}>
                        Impersonate
                    </MenuItem>
                )}
                {hasStatus('invited') && pstUserManagement && (
                    <MenuItem className="staff-member-menu-item" key="resendInvitation" onClick={onInviteResend}>
                        Resend Invite
                    </MenuItem>
                )}
                {showResetPassword && pstUserManagement && (
                    <MenuItem className="staff-member-menu-item" key="resetPassword" onClick={onResetPassword}>
                        Reset Password
                    </MenuItem>
                )}
                {(showDeactivate || showReactivate) && (
                    <MenuItem
                        key="activate"
                        className={classNames('active-button', 'staff-member-menu-item', {
                            active: hasStatus('active') || hasStatus('invited'),
                        })}
                        onClick={onToggleAccountStatus}
                    >
                        {showDeactivate ? 'Deactivate User' : 'Reactivate User'}
                    </MenuItem>
                )}
                {!hasStatus('invited') && hasStatus('deleted') && pstUserManagement && (
                    <MenuItem
                        key="activate"
                        className={classNames('active-button', 'staff-member-menu-item', {
                            active: hasStatus('active'),
                        })}
                        onClick={onRestoreAccount}
                    >
                        Restore User
                    </MenuItem>
                )}
                {!noDeleteOption && pstUserManagement && (
                    <MenuItem
                        key="delete"
                        className={classNames('active-button', 'staff-member-menu-item', {
                            active: !hasStatus('deleted'),
                        })}
                        onClick={onDeleteAccount}
                    >
                        Delete User
                    </MenuItem>
                )}
            </Menu>
            {showConfirmationBox && (
                <Modal
                    open={true}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            onCloseConfirmationBox()
                        }
                    }}
                    disableEscapeKeyDown={true}
                >
                    <div className="staff-member-confirmation-modal">
                        <div className="close-modal-button" onClick={onCloseConfirmationBox}>
                            <i className="material-icons">close</i>
                        </div>
                        <StaffConfirmationBox
                            employee={employee}
                            confirmFn={confirmationBoxCallbackFn}
                            closeFn={onCloseConfirmationBox}
                            type={confirmationBoxType}
                        />
                    </div>
                </Modal>
            )}
            {successBox && (
                <Modal
                    open={true}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            onCloseConfirmationBox()
                        }
                    }}
                    disableEscapeKeyDown={true}
                >
                    <div className="staff-member-confirmation-modal">
                        <div className="close-modal-button" onClick={onCloseSuccessBox}>
                            <i className="material-icons">close</i>
                        </div>
                        <StaffSuccessBox employee={employee} closeFn={onCloseSuccessBox} type={successBox} />
                    </div>
                </Modal>
            )}
        </div>
    )
}

export default StaffMemberActions
